import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchClaim as apiFetch,
  patchClaim as apiPatch,
  createDraft as apiCreate,
  toUiClaim,
} from '../api/claimsClient';

// Autosave is intentionally disabled. Saves only happen when the user
// explicitly invokes `flush` (Save / Continue buttons). setField/setMany
// mutate local state and mark fields dirty, but never schedule a network
// request. All previous debounce/in-flight coalescing machinery is unused.
//
// Nested-array invariant: callers must replace `service_lines` and `diagnoses`
// in full via `setField('service_lines', wholeArray)` — never index-paths like
// `setField('service_lines.0.field', v)`. setByPath does not handle numeric
// segments and would corrupt arrays into plain objects. buildPatchBody emits
// whatever shape the ref currently holds.

const CPT_RE = /^[A-Z0-9]{5}$/;
const ICD10_RE = /^[A-TV-Z][0-9][A-Z0-9](\.?[A-Z0-9]{1,4})?$/;
const MODIFIER_RE = /^[A-Z0-9]{2}$/;
const NPI_RE = /^\d{10}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const NPI_FIELD_NAMES = new Set([
  'npi', 'billing_provider_npi', 'rendering_provider_npi',
  'service_facility_npi', 'referring_provider_npi',
]);

/**
 * @param {string} field
 * @param {any} value
 * @returns {boolean}
 */
function isValidForWire(field, value) {
  if (value === '' || value == null) return true; // empty always sent (clears server)
  if (field === 'id') return UUID_RE.test(value); // drop synthetic ids ("dx_1", "sl_…")
  if (field === 'cpt_hcpcs_code') return CPT_RE.test(value);
  if (field === 'diagnosis_code') return ICD10_RE.test(value);
  if (field.startsWith('modifier_')) return MODIFIER_RE.test(value);
  if (NPI_FIELD_NAMES.has(field)) return NPI_RE.test(value);
  return true;
}

/**
 * Strip any nested object keys whose value is present-but-syntactically-invalid
 * for the backend's write validators. Returns a deep copy.
 * @param {any} obj
 * @returns {any}
 */
function stripPartials(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripPartials);
  const out = /** @type {Record<string, any>} */ ({});
  for (const [k, v] of Object.entries(obj)) {
    if (v && typeof v === 'object') {
      out[k] = stripPartials(v);
    } else if (isValidForWire(k, v)) {
      out[k] = v;
    }
    // else: drop the key entirely — field stays dirty in local state
  }
  return out;
}

/**
 * Diagnosis rows are all-or-nothing on the write API: a row without a valid,
 * non-blank code is not persistable. Treat blank or partial rows like removed
 * draft rows before PATCHing the full nested array.
 * @param {any} diagnoses
 * @returns {any[]}
 */
function normalizeDiagnosesForWire(diagnoses) {
  if (!Array.isArray(diagnoses)) return [];
  return diagnoses
    .map(stripPartials)
    .filter((row) => row && typeof row === 'object' && String(row.diagnosis_code || '').trim())
    .map((row, index) => ({ ...row, diagnosis_index: index + 1 }));
}

/**
 * @param {Record<string, any>} obj
 * @param {string} path
 * @param {any} value
 * @returns {Record<string, any>}
 */
function setByPath(obj, path, value) {
  const parts = path.split('.');
  const next = { ...obj };
  let cursor = next;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i];
    cursor[k] = { ...(cursor[k] || {}) };
    cursor = cursor[k];
  }
  cursor[parts[parts.length - 1]] = value;
  return next;
}

/**
 * @param {string|null} claimId
 */
export function useClaimDraft(claimId) {
  const [claim, setClaim] = useState(/** @type {Record<string, any>|null} */ (null));
  const [version, setVersion] = useState(0);
  const [draftStatus, setDraftStatus] = useState('draft');
  const [saveState, setSaveState] = useState('idle');
  const [saveError, setSaveError] = useState(/** @type {any} */ (null));
  const [lastSavedAt, setLastSavedAt] = useState(/** @type {Date|null} */ (null));
  const [conflict, setConflict] = useState(/** @type {{currentVersion: number}|null} */ (null));

  const dirtyRef = useRef(/** @type {Set<string>} */ (new Set()));
  const pendingClaimRef = useRef(/** @type {Record<string, any>|null} */ (null));
  const inFlightRef = useRef(false);

  const editable = draftStatus !== 'submitted';

  // Initial load — does NOT request server-side validation. Validation is
  // user-triggered (Validate button or Submit) per guided-flow design.
  useEffect(() => {
    if (!claimId) return;
    let cancelled = false;
    apiFetch(claimId).then((c) => {
      if (cancelled) return;
      setClaim(c);
      setVersion(c.version ?? 0);
      setDraftStatus(c.draft_status ?? 'draft');
      pendingClaimRef.current = c;
    });
    return () => { cancelled = true; };
  }, [claimId]);

  const refetch = useCallback(async () => {
    if (!claimId) return null;
    const c = await apiFetch(claimId);
    setClaim(c);
    setVersion(c.version ?? 0);
    setDraftStatus(c.draft_status ?? 'draft');
    pendingClaimRef.current = c;
    dirtyRef.current = new Set();
    return c;
  }, [claimId]);

  const buildPatchBody = useCallback(() => {
    if (!pendingClaimRef.current) return null;
    const dirty = dirtyRef.current;
    const body = /** @type {Record<string, any>} */ ({ version: pendingClaimRef.current.version ?? version });

    const dirtyTopLevel = new Set();
    for (const path of dirty) {
      dirtyTopLevel.add(path.split('.')[0]);
    }
    for (const top of dirtyTopLevel) {
      const value = /** @type {Record<string, any>} */ (pendingClaimRef.current)[top];
      body[top] = top === 'diagnoses' ? normalizeDiagnosesForWire(value) : stripPartials(value);
    }
    if (!pendingClaimRef.current.place_of_service) {
      body.place_of_service = '11';
    }
    if (Object.keys(body).length <= 1) return null;
    return body;
  }, [version]);

  // Manual save only. Returns true on success, false on failure (conflict,
  // 400, network). Callers (Save / Continue) await this and decide what to do
  // with the outcome.
  const flush = useCallback(async () => {
    if (!editable) return true;
    if (inFlightRef.current) return false;
    const body = buildPatchBody();
    if (!body || Object.keys(body).length <= 1) {
      setSaveState((s) => (s === 'pending' ? 'idle' : s));
      return true;
    }
    setSaveState('saving');
    inFlightRef.current = true;
    try {
      const updated = await apiPatch(/** @type {string} */ (claimId), body);
      setClaim(updated);
      setVersion(updated.version ?? version);
      setDraftStatus(updated.draft_status ?? draftStatus);
      pendingClaimRef.current = updated;
      dirtyRef.current = new Set();
      setSaveState('saved');
      setSaveError(null);
      setLastSavedAt(new Date());
      return true;
    } catch (err) {
      const status = /** @type {any} */ (err)?.response?.status;
      if (status === 409) {
        setConflict({ currentVersion: /** @type {any} */ (err).response.data?.current_version ?? null });
        setSaveState('error');
      } else if (status === 400) {
        setSaveError({ detail: /** @type {any} */ (err).response.data?.detail || 'Save failed', fieldErrors: /** @type {any} */ (err).response.data?.errors });
        setSaveState('error');
      } else {
        setSaveError({ detail: /** @type {any} */ (err)?.message || 'Network error' });
        setSaveState('error');
      }
      return false;
    } finally {
      inFlightRef.current = false;
    }
  }, [claimId, editable, buildPatchBody, version, draftStatus]);

  const setField = useCallback((/** @type {string} */ path, /** @type {any} */ value) => {
    if (!editable) return;
    if (!pendingClaimRef.current) return;
    const next = setByPath(pendingClaimRef.current, path, value);
    pendingClaimRef.current = next;
    setClaim(next);
    dirtyRef.current.add(path);
    setSaveState('pending');
  }, [editable]);

  const setMany = useCallback((/** @type {Record<string, any>} */ updates) => {
    if (!editable || !pendingClaimRef.current) return;
    let next = pendingClaimRef.current;
    for (const [path, value] of Object.entries(updates)) {
      next = setByPath(next, path, value);
      dirtyRef.current.add(path);
    }
    pendingClaimRef.current = next;
    setClaim(next);
    setSaveState('pending');
  }, [editable]);

  const selectPatient = useCallback((/** @type {Record<string, any>} */ patient) => {
    if (!editable || !patient) return;
    // Patient.gender is the long form ('male'|'female'|'other'), but
    // ClaimPatientSnapshot.gender is CMS-1500 Box 3 single-letter sex code
    // (max_length=1). Map before snapshotting; pass through 1-char values
    // already in CMS form. Empty stays empty.
    const rawGender = String(patient.gender || '').toLowerCase();
    const genderShort =
      rawGender === 'male' ? 'M' :
      rawGender === 'female' ? 'F' :
      rawGender === 'other' ? 'U' :
      rawGender.length === 1 ? rawGender.toUpperCase() :
      '';
    const snapshot = {
      first_name: patient.first_name || '',
      last_name: patient.last_name || '',
      middle_name: patient.middle_name || '',
      name_suffix: patient.name_suffix || '',
      date_of_birth: patient.date_of_birth || '',
      gender: genderShort,
      address_line_1: patient.address_line1 || '',
      address_line_2: patient.address_line2 || '',
      city: patient.city || '',
      state: patient.state || '',
      zip_code: patient.zip_code || '',
      phone: patient.phone || '',
    };
    /** @type {Record<string, any>} */
    const updates = {
      patient_id: patient.patient_id || patient.id,
      patient: snapshot,
      // Clear any stale account # / insured snapshot tied to a previous
      // patient — the picker is the moment of identity reset. Setting both
      // explicitly so DRF clears the server-side fields too. The branches
      // below may overwrite these with patient-specific values.
      patient_account_number: '',
    };
    const claim = pendingClaimRef.current;
    // Auto-populate Box 26 (patient account #) from the patient record.
    if (patient.account_number) {
      updates.patient_account_number = patient.account_number;
    }
    // When relationship is self (the default), the subscriber IS the patient;
    // mirror the patient identity into the insured snapshot so Box 4/7/11a
    // stay coherent with the picker. Always overwrite — stale values from a
    // previous patient must not leak through.
    const relCode = claim?.patient_relationship_to_insured || '18';
    if (relCode === '18') {
      updates.insured = {
        first_name: patient.first_name || '',
        last_name: patient.last_name || '',
        middle_name: patient.middle_name || '',
        name_suffix: patient.name_suffix || '',
        date_of_birth: patient.date_of_birth || '',
        gender: genderShort,
        address_line_1: patient.address_line1 || '',
        address_line_2: patient.address_line2 || '',
        city: patient.city || '',
        state: patient.state || '',
        zip_code: patient.zip_code || '',
        phone: patient.phone || '',
        // Preserve any member_id the user already typed against this insured.
        member_id: claim?.insured?.member_id || '',
        group_number: claim?.insured?.group_number || '',
      };
    }
    // Auto-populate the Insured/Subscriber snapshot (CMS-1500 boxes 1a, 4, 7,
    // 11/11a) from the patient's primary insurance record. Subscriber may be
    // the patient (relationship=self) or a different person; the row carries
    // explicit identity fields either way. Only fill when the claim has no
    // existing insured snapshot — preserves user overrides.
    const primary = (patient.insurance_records || []).find(
      (/** @type {any} */ r) => r.is_primary,
    ) || patient.insurance_records?.[0];
    const noInsuredYet = !claim?.insured || !claim.insured.member_id;
    if (primary && noInsuredYet) {
      // Map relationship to wire code (Box 6: 18=self, 01=spouse, 19=child, G8=other)
      const rel = (primary.subscriber_relationship || 'self').toLowerCase();
      /** @type {Record<string, string>} */
      const REL = { self: '18', spouse: '01', child: '19', other: 'G8' };
      updates.insured = {
        first_name: primary.subscriber_first_name || '',
        last_name: primary.subscriber_last_name || '',
        middle_name: primary.subscriber_middle_name || '',
        name_suffix: primary.subscriber_name_suffix || '',
        member_id: primary.subscriber_member_id || '',
        group_number: primary.group_number || '',
        date_of_birth: primary.subscriber_date_of_birth || '',
        gender: (primary.subscriber_gender || (rel === 'self' ? genderShort : '')).toUpperCase(),
        address_line_1: primary.subscriber_address_line_1 || '',
        address_line_2: primary.subscriber_address_line_2 || '',
        city: primary.subscriber_city || '',
        state: primary.subscriber_state || '',
        zip_code: primary.subscriber_zip_code || '',
        // subscriber_phone on the InsuranceRecord is often blank when the
        // subscriber IS the patient — fall back to the patient record phone
        // in that case so the self-sync above is not undone.
        phone: primary.subscriber_phone || (rel === 'self' ? patient.phone || '' : ''),
      };
      updates.patient_relationship_to_insured = REL[rel] || '18';
    }
    setMany(updates);
  }, [editable, setMany]);

  const resolveConflict = useCallback(async (/** @type {'reload'|'force'} */ mode) => {
    if (mode === 'reload') {
      await refetch();
      setConflict(null);
      setSaveState('idle');
      setSaveError(null);
      return;
    }
    if (mode === 'force' && conflict) {
      const body = buildPatchBody();
      if (!body || Object.keys(body).length <= 1) {
        setConflict(null);
        setSaveState('idle');
        setSaveError(null);
        return;
      }
      body.version = conflict.currentVersion;
      try {
        const updated = await apiPatch(/** @type {string} */ (claimId), /** @type {any} */ (body));
        setClaim(updated);
        setVersion(updated.version);
        setDraftStatus(updated.draft_status ?? draftStatus);
        pendingClaimRef.current = updated;
        dirtyRef.current = new Set();
        setConflict(null);
        setSaveState('saved');
        setLastSavedAt(new Date());
      } catch (err) {
        setSaveError({ detail: /** @type {any} */ (err)?.message || 'Force overwrite failed' });
        setSaveState('error');
      }
    }
  }, [refetch, conflict, buildPatchBody, claimId, draftStatus]);

  const resetDraft = useCallback(async () => {
    const fresh = await apiCreate();
    return fresh;
  }, []);

  // beforeunload guard
  useEffect(() => {
    if (dirtyRef.current.size === 0) return undefined;
    const handler = (/** @type {BeforeUnloadEvent} */ e) => {
      if (dirtyRef.current.size > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveState]);

  const ui = claim ? toUiClaim(claim) : null;
  const hasDirty = dirtyRef.current.size > 0 || saveState === 'pending';

  return {
    claim, ui, version, draftStatus, editable,
    setField, setMany, selectPatient,
    saveState, saveError, lastSavedAt, hasDirty,
    conflict, resolveConflict,
    flush, refetch, resetDraft,
  };
}
